import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Zap, Trash2, Eye, Mail, RefreshCw, Loader2, CheckSquare, XSquare, AlertTriangle, MessageSquare, RotateCcw, FolderOpen } from 'lucide-react';
import { getCampaigns, deleteCampaign, bulkDeleteCampaigns, bulkRetryCampaigns } from '../lib/api';
import api from '../lib/api';
import { toast } from 'sonner';

export default function Campaigns({ onOpenCampaign }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkRetrying, setBulkRetrying] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { loadCampaigns(); }, []);

  const loadCampaigns = async () => {
    try {
      const res = await getCampaigns();
      setCampaigns(res.data);
    } catch (err) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (id) => {
    setRetrying(id);
    try {
      await api.post(`/api/campaigns/${id}/retry`);
      toast.success('Pipeline restarted! Agents are working...');
      setTimeout(() => loadCampaigns(), 2000);
    } catch (err) {
      toast.error('Failed to retry campaign');
    } finally {
      setRetrying(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await deleteCampaign(id);
      setCampaigns(campaigns.filter(c => c.id !== id));
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      toast.success('Campaign deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Bulk selection
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === campaigns.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(campaigns.map(c => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const res = await bulkDeleteCampaigns(ids);
      const deleted = res.data.deleted || 0;
      setCampaigns(prev => prev.filter(c => !selectedIds.has(c.id)));
      setSelectedIds(new Set());
      setShowBulkDeleteConfirm(false);
      toast.success(`${deleted} campaign${deleted !== 1 ? 's' : ''} deleted successfully`);
    } catch (err) {
      toast.error('Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleBulkRetry = async () => {
    const failedIds = campaigns
      .filter(c => ['error', 'pipeline_error'].includes(c.status))
      .map(c => c.id);
    
    if (failedIds.length === 0) {
      toast.info('No failed campaigns to retry');
      return;
    }
    
    setBulkRetrying(true);
    try {
      const res = await bulkRetryCampaigns(failedIds);
      const retried = res.data.retried || 0;
      toast.success(`${retried} campaign${retried !== 1 ? 's' : ''} queued for retry`);
      setTimeout(() => loadCampaigns(), 2000);
    } catch (err) {
      toast.error('Bulk retry failed');
    } finally {
      setBulkRetrying(false);
    }
  };

  const statusBadge = (status) => {
    const styles = {
      pipeline_running: 'bg-[hsl(var(--accent)/0.15)] text-[hsl(var(--accent))] border border-[hsl(var(--accent)/0.25)]',
      content_review: 'bg-purple-500/15 text-purple-400 border border-purple-500/25',
      approved: 'bg-[hsl(var(--status-success)/0.15)] text-[hsl(var(--status-success))] border border-[hsl(var(--status-success)/0.25)]',
      sent: 'bg-[hsl(var(--status-success)/0.15)] text-[hsl(var(--status-success))] border border-[hsl(var(--status-success)/0.25)]',
      review_needed: 'bg-[hsl(var(--status-warning)/0.15)] text-[hsl(var(--status-warning))] border border-[hsl(var(--status-warning)/0.25)]',
      error: 'bg-[hsl(var(--status-danger)/0.15)] text-[hsl(var(--status-danger))] border border-[hsl(var(--status-danger)/0.25)]',
      sending: 'bg-[hsl(var(--accent)/0.15)] text-[hsl(var(--accent))] border border-[hsl(var(--accent)/0.25)]',
      scheduled: 'bg-[hsl(var(--chart-4)/0.15)] text-[hsl(var(--chart-4))] border border-[hsl(var(--chart-4)/0.25)]',
    };
    return <Badge className={`text-xs ${styles[status] || 'bg-secondary text-foreground'}`}>{status?.replace('_', ' ')}</Badge>;
  };

  const isAllSelected = campaigns.length > 0 && selectedIds.size === campaigns.length;
  const isSomeSelected = selectedIds.size > 0;

  return (
    <div className="page-container">
      {/* Hero Header */}
      <div className="page-hero" data-testid="campaigns-hero">
        <div className="relative z-10 px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand)/0.15)] border border-[hsl(var(--brand)/0.25)] flex items-center justify-center flex-shrink-0">
              <FolderOpen className="w-6 h-6 text-[hsl(var(--brand))]" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand))] font-semibold mb-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Campaign Management</p>
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>All Campaigns</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-0.5">Total</p>
              <p className="text-xl font-bold text-white font-mono">{campaigns.length}</p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            {(() => {
              const failedCount = campaigns.filter(c => ['error', 'pipeline_error'].includes(c.status)).length;
              if (failedCount > 0) return (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkRetry}
                  disabled={bulkRetrying}
                  data-testid="bulk-retry-btn"
                  className="gap-1.5 border-amber-500/30 text-amber-400 hover:bg-amber-500/10 btn-press"
                >
                  {bulkRetrying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                  Retry Failed ({failedCount})
                </Button>
              );
              return null;
            })()}
            {isSomeSelected && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowBulkDeleteConfirm(true)}
                data-testid="bulk-delete-btn"
                className="gap-1.5 btn-press"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete ({selectedIds.size})
              </Button>
            )}
            <Link to="/">
              <Button className="bg-[hsl(var(--accent-warm))] hover:bg-[hsl(var(--accent-warm)/0.88)] text-[hsl(var(--accent-warm-foreground))] btn-press" data-testid="new-campaign-btn">
                <Zap className="w-4 h-4 mr-2" /> New Campaign
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <Card className="bg-[hsl(var(--pane))] border border-white/10 animate-fade-in-up" style={{boxShadow: 'var(--shadow-elev-1)', animationDelay: '0.1s'}}>
        <CardContent className="p-0">
          {campaigns.length === 0 && !loading ? (
            <div className="text-center py-16">
              <Mail className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
              <p className="text-muted-foreground mb-4">No campaigns yet</p>
              <Link to="/campaigns/new">
                <Button className="bg-[hsl(var(--accent))] text-white">Create Your First Campaign</Button>
              </Link>
            </div>
          ) : (
            <Table data-testid="campaigns-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[44px]">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                      aria-label="Select all campaigns"
                    />
                  </TableHead>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Opens</TableHead>
                  <TableHead>Clicks</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((c) => (
                  <TableRow key={c.id} className={`table-row-hover cursor-pointer ${selectedIds.has(c.id) ? 'bg-accent/5' : ''}`} onClick={() => onOpenCampaign && onOpenCampaign(c)}>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.has(c.id)}
                        onCheckedChange={() => toggleSelect(c.id)}
                        data-testid={`select-campaign-${c.id}`}
                        aria-label={`Select campaign ${c.subject_line || c.topic}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{c.subject_line || c.topic}</p>
                        <p className="text-xs text-muted-foreground truncate max-w-[300px]">{c.topic}</p>
                      </div>
                    </TableCell>
                    <TableCell>{statusBadge(c.status)}</TableCell>
                    <TableCell className="font-mono text-sm">{c.metrics?.sent || 0}</TableCell>
                    <TableCell className="font-mono text-sm">{c.metrics?.opens || 0}</TableCell>
                    <TableCell className="font-mono text-sm">{c.metrics?.clicks || 0}</TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      {new Date(c.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        {(c.status === 'error' || (c.status === 'pipeline_running' && new Date() - new Date(c.created_at) > 600000)) && (
                          <Button variant="ghost" size="sm" onClick={() => handleRetry(c.id)} disabled={retrying === c.id} className="text-[hsl(var(--accent))]" data-testid={`retry-campaign-${c.id}`} title="Retry pipeline">
                            {retrying === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" data-testid={`view-campaign-${c.id}`} onClick={() => onOpenCampaign && onOpenCampaign(c)} title="Open Campaign">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} data-testid={`delete-campaign-${c.id}`}>
                          <Trash2 className="w-4 h-4 text-[hsl(var(--status-danger))]" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <DialogContent className="sm:max-w-[440px]" data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Delete {selectedIds.size} Campaign{selectedIds.size !== 1 ? 's' : ''}
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. All selected campaigns and their associated data (chat messages, pipeline results) will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-3">
              <p className="text-sm font-medium text-destructive">You are about to delete:</p>
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                  {selectedIds.size} campaign{selectedIds.size !== 1 ? 's' : ''}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-destructive/60" />
                  Associated chat messages and pipeline data
                </li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDeleteConfirm(false)} data-testid="bulk-delete-cancel">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              data-testid="bulk-delete-confirm"
            >
              {bulkDeleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</> : <><Trash2 className="w-4 h-4 mr-2" /> Delete All</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
